/* ═══════════════════════════════════════════════════════════════
   TasksBoard — the Kanban board + TaskCard. Native HTML5 drag stays
   (Decision: kept as-is, dnd-kit NOT added). Right-click "Move to" is the
   keyboard path for drag (Decision 5); drag + "Move to" + any status
   change are gated on `canEditTask(task)` (Decision 5/14).
   ═══════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import type { DragEvent } from 'react';
import type { Task, TaskStatus } from '../../../shared/types';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import EmptyState from '../../components/EmptyState';
import { rem } from '../../lib/rem';
import { isArchived } from '../../lib/archive';
import { ClipboardList, Plus, AlertTriangle } from 'lucide-react';
import { canDeleteTask, canEditTask } from './taskPermissions';
import { TaskRowMenu, type TaskRowMenuPoint } from './TaskRowMenu';
import { useConfirmDeleteTask } from './useConfirmDeleteTask';
import { STATUS_COLS, PRIORITY_COLORS, PRIORITY_LABELS } from './types';
import type { TasksData } from './useTasksData';
import { RecordLink } from '../../routing/RecordLink';
import { toAddress } from '../../routing/address';
import { openAddress } from '../../routing/router';
import { isWeb } from '../../lib/platform';

export interface TasksBoardProps {
  data: TasksData;
  tasks: Task[];
  perms: string[] | undefined;
  canCreateTask: boolean;
  canArchiveTask: boolean;
  onCardClick: (taskId: string) => void;
  onAddClick: (status: TaskStatus) => void;
}

function TaskCard({
  task,
  data,
  canEdit,
  onClick,
  onContextMenu,
}: {
  task: Task;
  data: TasksData;
  canEdit: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const assignees = data.getAssigneesForTask(task.id);
  const subtaskCount = data.getSubtaskCount(task.id);
  const subtasksDone = data.getSubtasksDone(task.id);
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';

  return (
    <RecordLink
      href={toAddress({ view: 'tasks', taskId: task.id })}
      onOpen={onClick}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e);
      }}
      draggable={canEdit}
      onDragStart={(e) => e.dataTransfer.setData('taskId', task.id)}
      title={canEdit ? undefined : 'You do not have permission to edit this task'}
      style={{
        padding: `${rem(10)} var(--space-3)`,
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: rem(8),
        cursor: canEdit ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: rem(6),
        transition: 'box-shadow .15s',
        borderLeft: `${rem(3)} solid ${PRIORITY_COLORS[task.priority]}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            color: 'var(--tx)',
            flex: 1,
            lineHeight: 1.3,
          }}
        >
          {task.title}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            padding: `${rem(1)} ${rem(5)}`,
            borderRadius: rem(3),
            background: `color-mix(in srgb, ${PRIORITY_COLORS[task.priority]} 20%, transparent)`,
            color: PRIORITY_COLORS[task.priority],
            flexShrink: 0,
            textTransform: 'uppercase',
          }}
        >
          {PRIORITY_LABELS[task.priority]}
        </span>
      </div>

      {(() => {
        const pIds = data.getProjectIdsForTask(task.id);
        if (pIds.length === 0) return null;
        return (
          <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
            {pIds.map((pid) => (
              <span
                key={pid}
                style={{
                  fontSize: 'var(--font-size-sm)',
                  padding: `${rem(1)} ${rem(6)}`,
                  borderRadius: rem(8),
                  background: 'color-mix(in srgb, var(--ac) 15%, transparent)',
                  color: 'var(--tm)',
                  fontWeight: 500,
                }}
              >
                {data.getProjectName(pid)}
              </span>
            ))}
          </div>
        );
      })()}

      <div style={{ display: 'flex', alignItems: 'center', gap: rem(6), flexWrap: 'wrap' }}>
        {assignees.map((m) => (
          <span
            key={m.id}
            style={{
              fontSize: 'var(--font-size-xs)',
              padding: `${rem(1)} ${rem(6)}`,
              borderRadius: 'var(--radius-lg)',
              background: 'var(--sa)',
              color: 'var(--tm)',
              fontWeight: 500,
            }}
          >
            {m.name
              .split(' ')
              .map((n) => n[0])
              .join('')}
          </span>
        ))}
        {subtaskCount > 0 && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>
            {subtasksDone}/{subtaskCount} subtasks
          </span>
        )}
        {task.dueDate && (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: isOverdue ? 'var(--dg)' : 'var(--tm)',
              fontWeight: isOverdue ? 600 : 400,
              marginLeft: 'auto',
            }}
          >
            {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
    </RecordLink>
  );
}

export function TasksBoard({
  data,
  tasks,
  perms,
  canCreateTask,
  canArchiveTask,
  onCardClick,
  onAddClick,
}: TasksBoardProps) {
  const [menu, setMenu] = useState<{ point: TaskRowMenuPoint; task: Task } | null>(null);
  const { requestDelete, confirmDeleteDialog } = useConfirmDeleteTask(data);
  const loading = !data.projectsLoaded && data.tasks.length === 0;

  function canEdit(task: Task): boolean {
    return canEditTask(perms, data.userId, data.members, task, data.assignments, data.tasks);
  }

  function handleDrop(e: DragEvent, status: TaskStatus) {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId) return;
    const task = tasks.find((t) => t.id === taskId);
    if (task && !canEdit(task)) return;
    data.handleStatusChange(taskId, status);
  }

  if (data.loadError) {
    return (
      <div style={{ flex: 1, display: 'flex' }}>
        <EmptyState
          icon={<Icon icon={AlertTriangle} em={2} aria-hidden />}
          title="Tasks did not load"
          subtitle="Check your connection and try again."
          action={{ label: 'Try again', onClick: () => void data.retryLoad() }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', gap: 'var(--space-3)', flex: 1 }}>
        {STATUS_COLS.map((col) => (
          <div
            key={col.id}
            className="animate-pulse"
            style={{
              flex: 1,
              minWidth: rem(240),
              minHeight: rem(200),
              borderRadius: 'var(--radius-lg)',
              background: 'var(--sa)',
              border: '1px solid var(--bd)',
            }}
          />
        ))}
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex' }}>
        <EmptyState
          icon={<Icon icon={ClipboardList} em={2} aria-hidden />}
          title="No tasks"
          subtitle="Create a task to get started, or clear any active filters."
          action={
            canCreateTask ? { label: 'New task', onClick: () => onAddClick('todo') } : undefined
          }
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        flex: 1,
        overflow: 'auto',
        padding: '0 0 var(--space-4)',
      }}
    >
      {STATUS_COLS.map((col) => {
        const colTasks = tasks.filter((t) => t.status === col.id);
        return (
          <div
            key={col.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, col.id)}
            style={{
              flex: 1,
              minWidth: rem(240),
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--sa)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--bd)',
            }}
          >
            <div
              style={{
                padding: `${rem(10)} ${rem(14)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid var(--bd)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: rem(6) }}>
                <span
                  style={{
                    width: 'var(--space-2)',
                    height: 'var(--space-2)',
                    borderRadius: '50%',
                    background: col.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--tx)' }}
                >
                  {col.label}
                </span>
                <span
                  style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', fontWeight: 600 }}
                >
                  {colTasks.length}
                </span>
              </div>
              {canCreateTask && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onAddClick(col.id)}
                  aria-label={`Add task to ${col.label}`}
                  title={`Add task to ${col.label}`}
                >
                  <Icon icon={Plus} em={1.0769} />
                </Button>
              )}
            </div>
            <div
              style={{
                padding: 'var(--space-2)',
                display: 'flex',
                flexDirection: 'column',
                gap: rem(6),
                flex: 1,
                overflow: 'auto',
              }}
            >
              {colTasks.map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  data={data}
                  canEdit={canEdit(t)}
                  onClick={() => onCardClick(t.id)}
                  onContextMenu={(e) => setMenu({ point: { x: e.clientX, y: e.clientY }, task: t })}
                />
              ))}
              {colTasks.length === 0 && (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    textAlign: 'center',
                    color: 'var(--tm)',
                    fontSize: 'var(--font-size-sm)',
                    opacity: 0.6,
                  }}
                >
                  Drop tasks here
                </div>
              )}
            </div>
          </div>
        );
      })}

      {menu && (
        <TaskRowMenu
          point={menu.point}
          currentStatus={menu.task.status}
          isArchived={isArchived(menu.task)}
          canEdit={canEdit(menu.task)}
          canArchive={canArchiveTask}
          canDelete={canDeleteTask(perms)}
          onClose={() => setMenu(null)}
          onOpen={() => onCardClick(menu.task.id)}
          onOpenNewWindow={
            isWeb
              ? undefined
              : () =>
                  openAddress(toAddress({ view: 'tasks', taskId: menu.task.id }), {
                    newWindow: true,
                  })
          }
          onMoveTo={(status) => data.handleStatusChange(menu.task.id, status)}
          onArchiveToggle={() => void data.doArchiveTasks([menu.task], !isArchived(menu.task))}
          onDelete={() => requestDelete(menu.task)}
        />
      )}

      {confirmDeleteDialog}
    </div>
  );
}
